import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { isAppUpdateAvailable, getAvailableAppUpdateVersion, openAppStoreForUpdate } from '../../services/app-update';
import { t } from 'src/i18n';
import '../../library/fields/d-expansion.js';
import '../../library/elements/d-action.js';

/**
 * Banner that displays when a native app update is available in the App Store/Google Play.
 * Checks localStorage on mount and listens for storage events to update.
 *
 * Usage:
 * <d-app-update-banner></d-app-update-banner>
 *
 * Shows a banner at the top of the screen with:
 * - Message about available update
 * - "Oppdater nå" button to open store
 * - "Senere" button to dismiss
 */
@customElement('d-app-update-banner')
export class DAppUpdateBanner extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      position: fixed;
      top: var(--safe-area-inset-top, 0px);
      left: 0;
      right: 0;
      z-index: 1000;
    }

    #banner {
      background: var(--themeColorDarkerOne);
      color: white;
      padding: 16px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }

    .content {
      flex: 1;
      min-width: 0;
    }

    .message {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 4px;
      line-height: 1.4;
    }

    .version {
      font-size: 13px;
      opacity: 0.9;
    }

    .actions {
      display: flex;
      gap: 8px;
      flex-shrink: 0;
    }

    button {
      padding: 10px 16px;
      border: none;
      border-radius: 6px;
      font-family: var(--mainSans);
      font-size: 14px;
      color: white;
    }

    button.primary {
      background: hsla(0, 0%, 0%, 0.17);
      font-weight: 500;
    }

    button.secondary {
      background: hsla(0, 0%, 0%, 0.1);
    }

    @media only screen and (max-width: 600px) {
      #banner {
        flex-direction: column;
        align-items: stretch;
        padding: 14px 16px;
      }

      .actions {
        width: 100%;
      }

      button {
        flex: 1;
      }
    }
  `;

  @property({ type: Boolean })
  show = false;

  @state()
  version = '';

  connectedCallback() {
    super.connectedCallback();
    this._checkForUpdate();

    // Listen for storage changes (when app resume updates localStorage)
    window.addEventListener('storage', this._onStorageChange);
    // Also listen for custom event that can be dispatched when update info changes
    window.addEventListener('app-update-available', this._checkForUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('storage', this._onStorageChange);
    window.removeEventListener('app-update-available', this._checkForUpdate);
  }

  private _onStorageChange = () => {
    this._checkForUpdate();
  };

  private _checkForUpdate = () => {
    this.show = isAppUpdateAvailable();
    if (this.show) {
      this.version = getAvailableAppUpdateVersion() || '';
    }
  };

  private async _onUpdateNow() {
    try {
      await openAppStoreForUpdate();
    } catch (error) {
      console.error('[AppUpdateBanner] Failed to open store:', error);
    }
  }

  private _onDismiss() {
    this.show = false;
    // Could store dismissal in localStorage to not show again for some time
    localStorage.setItem('appUpdateDismissedAt', new Date().toISOString());
  }

  render() {
    if (!this.show) {
      return nothing;
    }

    return html`
      <d-expansion ?opened=${this.show}>
        <div id="banner">
          <div class="content">
            <div class="message">${t('appUpdateBanner.updateAvailable')}</div>
            ${this.version
              ? html`<div class="version">${t('appUpdateBanner.version', { version: this.version })}</div>`
              : nothing}
          </div>
          <div class="actions">
            <button class="primary" @click=${this._onUpdateNow}>${t('appUpdateBanner.updateNow')}</button>
            <button class="secondary" @click=${this._onDismiss}>${t('appUpdateBanner.later')}</button>
          </div>
        </div>
      </d-expansion>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-app-update-banner': DAppUpdateBanner;
  }
}
